import React, { useState } from 'react';
import { 
  Upload, 
  Download, 
  FileText, 
  AlertCircle, 
  CheckCircle,
  Package,
  Truck,
  Building2,
  Activity
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { importExportService } from '../../services/importExportService';
import { ImportResult, CSVTemplate } from '../../types';

function ImportExportPage() {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const [activeTab, setActiveTab] = useState<'import' | 'export'>('import');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [loading, setLoading] = useState(false);

  const templates = importExportService.getCSVTemplates();

  const handleDownloadTemplate = async (templateId: string) => {
    try {
      const url = await importExportService.downloadCSVTemplate(templateId);
      const link = document.createElement('a');
      link.href = url;
      link.download = `template_${templateId}.csv`;
      link.click();
      
      addNotification({
        userId: user?.id || '',
        title: 'Template téléchargé',
        message: 'Le modèle CSV a été téléchargé avec succès',
        type: 'success'
      });
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur de téléchargement',
        message: 'Impossible de télécharger le modèle',
        type: 'error'
      });
    }
  };

  const handleImport = async () => {
    if (!importFile || !selectedTemplate || !user) return;

    try {
      setLoading(true);
      const csvContent = await importFile.text();
      
      let result: ImportResult;
      
      switch (selectedTemplate) {
        case 'products':
          result = await importExportService.importProducts(csvContent, user.id);
          break;
        case 'suppliers':
          result = await importExportService.importSuppliers(csvContent, user.id);
          break;
        case 'inventory':
          result = await importExportService.importInventory(csvContent, 'session-demo', user.id);
          break;
        default:
          throw new Error('Type d\'import non supporté');
      }
      
      setImportResult(result);
      
      if (result.errors === 0) {
        addNotification({
          userId: user.id,
          title: 'Import réussi',
          message: `${result.success} élément(s) importé(s) avec succès`,
          type: 'success'
        });
      } else {
        addNotification({
          userId: user.id,
          title: 'Import terminé avec erreurs',
          message: `${result.success} réussis, ${result.errors} erreurs`,
          type: 'warning'
        });
      }
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur d\'import',
        message: error instanceof Error ? error.message : 'Erreur inconnue',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (type: string) => {
    if (!user) return;

    try {
      setLoading(true);
      let csvContent = '';
      let filename = '';
      
      switch (type) {
        case 'products':
          csvContent = await importExportService.exportProducts();
          filename = 'produits';
          break;
        case 'suppliers':
          csvContent = await importExportService.exportSuppliers();
          filename = 'fournisseurs';
          break;
        case 'stock_movements':
          csvContent = await importExportService.exportStockMovements();
          filename = 'mouvements_stock';
          break;
        case 'chantiers':
          csvContent = await importExportService.exportChantiers();
          filename = 'chantiers';
          break;
        default:
          throw new Error('Type d\'export non supporté');
      }
      
      await importExportService.downloadCSV(csvContent, filename);
      
      addNotification({
        userId: user.id,
        title: 'Export réussi',
        message: 'Les données ont été exportées avec succès',
        type: 'success'
      });
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur d\'export',
        message: 'Impossible d\'exporter les données',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const getTemplateIcon = (type: string) => {
    switch (type) {
      case 'products':
        return <Package className="h-5 w-5" />;
      case 'suppliers':
        return <Truck className="h-5 w-5" />;
      case 'chantiers':
        return <Building2 className="h-5 w-5" />;
      case 'inventory':
        return <Activity className="h-5 w-5" />;
      default:
        return <FileText className="h-5 w-5" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card rounded-xl card-hover">
        <div className="px-6 py-6 sm:p-8">
          <div className="flex items-center space-x-4">
            <div className="p-2 bg-gradient-to-br from-green-500 to-green-700 rounded-lg shadow-lg">
              <FileText className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Import / Export CSV</h1>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="glass-card rounded-xl">
        <div className="px-6 py-4 border-b border-gray-200/50">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('import')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'import'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Upload className="h-4 w-4 inline mr-2" />
              Import CSV
            </button>
            <button
              onClick={() => setActiveTab('export')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'export'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Download className="h-4 w-4 inline mr-2" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="min-h-96">
          {activeTab === 'import' ? (
            <div className="p-6 space-y-6">
              {/* Templates disponibles */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  1. Télécharger un modèle CSV
                </h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {templates.map((template) => (
                    <div key={template.id} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-center mb-3">
                        <div className="p-2 bg-gray-100 rounded-lg mr-3">
                          {getTemplateIcon(template.type)}
                        </div>
                        <h4 className="text-sm font-medium text-gray-900">
                          {template.name}
                        </h4>
                      </div>
                      
                      <p className="text-xs text-gray-600 mb-3">
                        {template.description}
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                          {template.headers.length} colonnes
                        </span>
                        <button
                          onClick={() => handleDownloadTemplate(template.id)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Télécharger
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Upload section */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  2. Importer votre fichier CSV
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type d'import
                    </label>
                    <select
                      value={selectedTemplate}
                      onChange={(e) => setSelectedTemplate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">Sélectionner un type</option>
                      {templates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fichier CSV
                    </label>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  
                  <button
                    onClick={handleImport}
                    disabled={!importFile || !selectedTemplate || loading}
                    className="w-full sm:w-auto px-6 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    <span>{loading ? 'Import en cours...' : 'Importer'}</span>
                  </button>
                </div>
              </div>

              {/* Import Results */}
              {importResult && (
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Résultats de l'import
                  </h3>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                    <div className="bg-green-100 rounded-lg p-3 text-center">
                      <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-1" />
                      <div className="text-lg font-semibold text-green-800">
                        {importResult.success}
                      </div>
                      <div className="text-sm text-green-600">Réussis</div>
                    </div>
                    
                    <div className="bg-red-100 rounded-lg p-3 text-center">
                      <AlertCircle className="h-6 w-6 text-red-600 mx-auto mb-1" />
                      <div className="text-lg font-semibold text-red-800">
                        {importResult.errors}
                      </div>
                      <div className="text-sm text-red-600">Erreurs</div>
                    </div>
                    
                    <div className="bg-yellow-100 rounded-lg p-3 text-center">
                      <AlertCircle className="h-6 w-6 text-yellow-600 mx-auto mb-1" />
                      <div className="text-lg font-semibold text-yellow-800">
                        {importResult.warnings}
                      </div>
                      <div className="text-sm text-yellow-600">Avertissements</div>
                    </div>
                  </div>
                  
                  {importResult.details.length > 0 && (
                    <div className="max-h-64 overflow-y-auto">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Détails :</h4>
                      <div className="space-y-1">
                        {importResult.details.map((detail, index) => (
                          <div
                            key={index}
                            className={`text-sm p-2 rounded ${
                              detail.type === 'error' 
                                ? 'bg-red-50 text-red-700' 
                                : 'bg-yellow-50 text-yellow-700'
                            }`}
                          >
                            <span className="font-medium">Ligne {detail.line}:</span> {detail.message}
                            {detail.field && <span className="text-xs"> (champ: {detail.field})</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-6">
                Exporter vos données
              </h3>
              
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-4">
                    <Package className="h-8 w-8 text-green-500 mr-3" />
                    <h4 className="text-lg font-medium text-gray-900">Produits</h4>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Export de tous les produits avec stocks, catégories et emplacements
                  </p>
                  <button
                    onClick={() => handleExport('products')}
                    disabled={loading}
                    className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    Exporter
                  </button>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-4">
                    <Truck className="h-8 w-8 text-blue-500 mr-3" />
                    <h4 className="text-lg font-medium text-gray-900">Fournisseurs</h4>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Export de tous les fournisseurs avec coordonnées complètes
                  </p>
                  <button
                    onClick={() => handleExport('suppliers')}
                    disabled={loading}
                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    Exporter
                  </button>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-4">
                    <Activity className="h-8 w-8 text-purple-500 mr-3" />
                    <h4 className="text-lg font-medium text-gray-900">Mouvements de stock</h4>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Export du journal des mouvements de stock avec détails
                  </p>
                  <button
                    onClick={() => handleExport('stock_movements')}
                    disabled={loading}
                    className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    Exporter
                  </button>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-4">
                    <Building2 className="h-8 w-8 text-orange-500 mr-3" />
                    <h4 className="text-lg font-medium text-gray-900">Chantiers</h4>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Export de tous les chantiers avec informations complètes
                  </p>
                  <button
                    onClick={() => handleExport('chantiers')}
                    disabled={loading}
                    className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    Exporter
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ImportExportPage;